import { csvCell } from '~/utils/csvCell';

export type Row = Record<string, unknown>;
export type Measure = { key: string; label: string; kind?: 'percent' | 'money' | 'number' };
export const number = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const parsed = Number(typeof value === 'object' && 'value' in value ? value.value : value);
  return Number.isFinite(parsed) ? parsed : null;
};
export const ratio = (a: unknown, b: unknown) => {
  const numerator = number(a),
    denominator = number(b);
  return numerator === null || denominator === null || denominator === 0 ? null : numerator / denominator;
};
export function commsMetrics(row: Row, unique: boolean): Row {
  const sends = number(row[unique ? 'unique_send_cnt' : 'send_cnt']);
  const opens = number(row[unique ? 'unique_open_cnt' : 'open_cnt']);
  const clicks = number(row[unique ? 'unique_click_cnt' : 'click_cnt']);
  const unsubs = number(row[unique ? 'unique_unsubscribe_cnt' : 'unsubscribe_cnt']);
  const sessions = number(row[unique ? 'unique_sessions' : 'sessions']);
  const orders = number(row.orders),
    ttv = number(row.ttv),
    gm = number(row.gm);
  return {
    ...row,
    sends,
    opens,
    clicks,
    unsubs,
    sessions,
    orders,
    ttv,
    gm,
    open_rate: ratio(opens, sends),
    click_rate: ratio(clicks, sends),
    click_to_open: ratio(clicks, opens),
    unsubscribe_rate: ratio(unsubs, sends),
    session_ratio: ratio(sessions, clicks),
    sessions_per_send: ratio(sessions, sends),
    purchase_rate: ratio(orders, clicks),
    gm_per_send: ratio(gm, sends),
    aov: ratio(ttv, orders),
    ttv_per_send: ratio(ttv, sends),
    ttv_per_click: ratio(ttv, clicks),
  };
}
export function luxMetrics(row: Row): Row {
  const added = number(row.added_margin),
    lost = number(row.lost_margin),
    fees = number(row.attributed_fees);
  return {
    ...row,
    takeup_rate: ratio(row.takeups, row.eligible_orders),
    takeup_packages_per_order: ratio(row.takeup_packages, row.takeups),
    member_items_per_order: ratio(row.member_items, row.member_orders) ?? 0,
    member_packages_per_order: ratio(row.member_packages, row.margin_member_orders) ?? 0,
    savings_per_package: ratio(row.saving_amount, row.saving_packages) ?? 0,
    margin_impact_per_package: ratio(added === null || lost === null ? null : added + lost, row.saving_packages) ?? 0,
    member_package_share: ratio(row.member_packages, row.eligible_packages) ?? 0,
    first_renewal_rate: ratio(row.first_renewed, row.first_decided),
    second_renewal_rate: ratio(row.second_renewed, row.second_decided),
    first_estimated_renewal_rate: ratio(row.first_future_likely, row.first_future_base),
    second_estimated_renewal_rate: ratio(row.second_future_likely, row.second_future_base),
    renewal_rate: ratio(row.renewed, row.decided),
    estimated_renewal_rate: ratio(row.future_likely, row.future_base),
    average_savings: ratio(row.savings_sum, row.savings_count),
    average_orders: ratio(row.orders_sum, row.orders_count),
    average_cancel_days: ratio(row.days_sum, row.days_count),
    gross_margin_impact: added === null || lost === null ? null : added + lost,
    total_margin_impact: added === null || lost === null ? null : added + lost + (fees ?? 0),
    executive_takeup: ratio(row.lp_takeup_rate_numerator, row.lp_takeup_rate_denominator),
    executive_churn: ratio(row.lp_churned_members, row.lp_renewal_ops),
  };
}
export function csv(rows: Array<Row>, columns: Array<Measure>, metadata: Record<string, unknown>): string {
  const cell = (value: unknown) => csvCell(value, 'Unknown');
  return (
    '\uFEFF' +
    [...Object.entries(metadata), columns.map((c) => c.label), ...rows.map((row) => columns.map((c) => row[c.key]))]
      .map((row) => row.map(cell).join(','))
      .join('\r\n')
  );
}
export function formatted(value: unknown, kind: Measure['kind'] = 'number'): string {
  const n = number(value);
  return n === null
    ? 'Unknown'
    : new Intl.NumberFormat('en-AU', {
        style: kind === 'percent' ? 'percent' : kind === 'money' ? 'currency' : 'decimal',
        currency: kind === 'money' ? 'AUD' : undefined,
        maximumFractionDigits: kind === 'number' ? 2 : 2,
      }).format(n);
}
export const commsMeasures: Array<Measure> = [
  ['sends', 'Sends'],
  ['opens', 'Opens'],
  ['clicks', 'Clicks'],
  ['unsubs', 'Unsubscribes'],
  ['open_rate', 'Open rate', 'percent'],
  ['click_rate', 'Click rate', 'percent'],
  ['click_to_open', 'Clicks / opens', 'percent'],
  ['unsubscribe_rate', 'Unsubscribe rate', 'percent'],
  ['sessions', 'Sessions'],
  ['session_ratio', 'Sessions / clicks', 'percent'],
  ['sessions_per_send', 'Sessions / sends', 'percent'],
  ['orders', 'Orders'],
  ['purchase_rate', 'Purchase rate', 'percent'],
  ['gm', 'GM', 'money'],
  ['gm_per_send', 'GM / send', 'money'],
  ['ttv', 'TTV', 'money'],
  ['aov', 'AOV', 'money'],
  ['ttv_per_send', 'TTV / send', 'money'],
  ['ttv_per_click', 'TTV / click', 'money'],
].map(([key, label, kind]) => ({ key, label, kind: kind as Measure['kind'] }));

/** Sum observed monthly cohort averages; never invent an unobserved maturity cell. */
export function cohortCumulative(rows: Array<Row>, metric: string): Array<Row> {
  const cohorts = [...new Set(rows.map((row) => String(row.bucket)))].sort();
  return cohorts.flatMap((cohort) => {
    const observed = rows
      .filter((row) => String(row.bucket) === cohort)
      .sort((a, b) => Number(a.month_offset) - Number(b.month_offset));
    let total: number | null = null;
    return observed.map((row) => {
      const value = number(row[metric]);
      if (value !== null) total = (total ?? 0) + value;
      return { ...row, cumulative: value === null ? null : total };
    });
  });
}
